import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// IMPORTANT: `withCredentials: false` on purpose.
// The Kubernetes/Cloudflare ingress in front of FastAPI rewrites CORS headers
// to `Access-Control-Allow-Origin: *` AND `Access-Control-Allow-Credentials: true`,
// which browsers reject as an invalid combination on credentialed requests
// (the response is blocked client-side → axios receives no response → the user
// sees a generic "Connexion lente ou interrompue" message, especially on
// mobile Safari which enforces CORS strictly).
//
// Auth therefore relies on Bearer tokens stored in localStorage
// (key: `helencia_token`). Every auth-creating endpoint — `/auth/login`,
// `/auth/session`, `/auth/verify-email` — returns a `session_token` that the
// pages persist into localStorage. The request interceptor below re-attaches
// it as `Authorization: Bearer …` on every non-public-auth call. The backend
// `get_current_user` dependency reads either the cookie OR the Bearer header,
// so cookieless usage is fully supported.
export const api = axios.create({
  baseURL: API,
  withCredentials: false,
  timeout: 60000, // 60s — mobile networks need headroom (3G/Edge in elevators etc.)
});

// De-duplicate identical in-flight GETs (several widgets on a dashboard often
// request the same endpoint at mount) — they share one network round-trip.
const inflight = new Map();
const defaultAdapter = axios.getAdapter(api.defaults.adapter);
api.defaults.adapter = (config) => {
  if ((config.method || "get").toLowerCase() !== "get") return defaultAdapter(config);
  const key = `${config.url}|${JSON.stringify(config.params || {})}|${config.headers?.Authorization || ""}`;
  if (inflight.has(key)) return inflight.get(key).then((r) => ({ ...r, config }));
  const p = defaultAdapter(config).finally(() => setTimeout(() => inflight.delete(key), 300));
  inflight.set(key, p);
  return p;
};

// Bearer token fallback for mobile cookie issues (Safari ITP, etc.)
// IMPORTANT: do NOT attach Authorization to public auth endpoints — a stale or
// malformed token here would cause unnecessary 401s and add latency on mobile.
const PUBLIC_AUTH_PATHS = [
  "/auth/register",
  "/auth/login",
  "/auth/session",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/verify-email",
  "/auth/resend-verification",
];

api.interceptors.request.use((config) => {
  const url = config.url || "";
  const isPublicAuth = PUBLIC_AUTH_PATHS.some((p) => url.startsWith(p));
  if (!isPublicAuth) {
    const token = localStorage.getItem("helencia_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Centralised error normalisation. Network errors and 5xx get a stable
// `error.userMessage` so callers can show a toast without crashing the page.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    let userMessage = typeof detail === "string" ? detail : null;
    if (!userMessage) {
      if (!error.response) {
        userMessage = "Connexion lente ou interrompue. Réessayez dans un instant.";
      } else if (status === 401) {
        userMessage = "Session expirée. Reconnectez-vous.";
      } else if (status === 403) {
        userMessage = "Action non autorisée.";
      } else if (status === 404) {
        userMessage = "Ressource introuvable.";
      } else if (status === 413) {
        userMessage = "Fichier trop volumineux.";
      } else if (status === 429) {
        userMessage = "Trop de requêtes. Réessayez dans un instant.";
      } else if (status >= 500) {
        userMessage = "Erreur serveur. Notre équipe a été notifiée.";
      } else {
        userMessage = "Une erreur est survenue.";
      }
    }
    error.userMessage = userMessage;
    return Promise.reject(error);
  }
);

export default api;
